// Klien API Strukturku: auth JWT (token di localStorage) + workspace per-user.
const BASE = process.env.REACT_APP_BACKEND_URL as string;
const TOKEN_KEY = "strukturku.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string) {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export interface ApiError { status: number; detail?: any; }

export function formatApiError(detail: any): string {
  if (detail == null) return "Terjadi kesalahan. Coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function req(path: string, opts: RequestInit = {}): Promise<any> {
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(opts.headers as any || {}) };
  const t = getToken();
  if (t) headers["Authorization"] = "Bearer " + t;
  const res = await fetch(BASE + path, { ...opts, headers });
  let data: any = null;
  try { data = await res.json(); } catch { /* no body */ }
  if (!res.ok) throw { status: res.status, detail: data?.detail } as ApiError;
  return data;
}

export interface User { id: string; email: string; name: string; role: "admin" | "consultant" | "viewer"; created_at?: string; }

export const authApi = {
  login: (email: string, password: string) =>
    req("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  register: (email: string, password: string, name: string) =>
    req("/api/auth/register", { method: "POST", body: JSON.stringify({ email, password, name }) }),
  me: (): Promise<User> => req("/api/auth/me"),
  forgotPassword: (email: string): Promise<{ ok: boolean; message: string; test_mode?: boolean; dev_token?: string; dev_link?: string }> =>
    req("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: (token: string, new_password: string): Promise<{ ok: boolean; message: string }> =>
    req("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, new_password }) }),
  listUsers: (): Promise<User[]> => req("/api/auth/users"),
  createUser: (b: { email: string; password: string; name: string; role: string }): Promise<User> =>
    req("/api/auth/users", { method: "POST", body: JSON.stringify(b) }),
  setRole: (uid: string, role: string) =>
    req(`/api/auth/users/${uid}/role`, { method: "PATCH", body: JSON.stringify({ role }) }),
  deleteUser: (uid: string) => req(`/api/auth/users/${uid}`, { method: "DELETE" }),
};

export const wsApi = {
  get: (): Promise<{ state: any; updated_at: string | null }> => req("/api/strukturku/workspace"),
  save: (state: any) => req("/api/strukturku/workspace", { method: "PUT", body: JSON.stringify({ state }) }),
};

export interface ActivityItem {
  user_name: string; user_email: string; role: string; action: string;
  target_type: string; target_name: string; group_id: string; group_name: string;
  meta: any; ip: string; created_at: string;
}
export const activityApi = {
  log: (b: { action: string; target_type?: string; target_name?: string; group_id?: string; group_name?: string; meta?: any }) =>
    req("/api/strukturku/activity", { method: "POST", body: JSON.stringify(b) }).catch(() => null),
  list: (p: { limit?: number; skip?: number; action?: string; q?: string; date_from?: string; date_to?: string } = {}): Promise<{ items: ActivityItem[]; total: number }> => {
    const qs = new URLSearchParams();
    if (p.limit != null) qs.set("limit", String(p.limit));
    if (p.skip != null) qs.set("skip", String(p.skip));
    if (p.action) qs.set("action", p.action);
    if (p.q) qs.set("q", p.q);
    if (p.date_from) qs.set("date_from", p.date_from);
    if (p.date_to) qs.set("date_to", p.date_to);
    return req("/api/strukturku/activity?" + qs.toString());
  },
};

export const shareApi = {
  get: (gid: string): Promise<{ share: { token: string; created_at: string; expires_at?: string | null } | null }> =>
    req(`/api/strukturku/shares?gid=${encodeURIComponent(gid)}`),
  create: (gid: string, expires_days?: number | null): Promise<{ token: string; created_at: string; expires_at?: string | null }> =>
    req("/api/strukturku/shares", { method: "POST", body: JSON.stringify({ gid, expires_days: expires_days ?? null }) }),
  revoke: (gid: string): Promise<{ ok: boolean }> =>
    req(`/api/strukturku/shares/${encodeURIComponent(gid)}`, { method: "DELETE" }),
  public: (token: string): Promise<{ name: string; group: any; overrides: any }> =>
    req(`/api/strukturku/public/${encodeURIComponent(token)}`),
};

export const settingsApi = {
  get: (): Promise<{ activity_retention_months: number }> => req("/api/strukturku/settings"),
  save: (activity_retention_months: number): Promise<{ activity_retention_months: number }> =>
    req("/api/strukturku/settings", { method: "PUT", body: JSON.stringify({ activity_retention_months }) }),
};

export interface ArchiveItem {
  id: string; period_from: string; period_to: string; count: number;
  filename: string; kind: string; created_at: string; created_by: string; storage_path: string;
}
export const archiveApi = {
  list: (): Promise<{ items: ArchiveItem[] }> => req("/api/strukturku/activity/archives"),
  create: (date_from?: string, date_to?: string): Promise<{ id: string; period_from: string; period_to: string; count: number; filename: string }> =>
    req("/api/strukturku/activity/archive", { method: "POST", body: JSON.stringify({ date_from: date_from || "", date_to: date_to || "" }) }),
  downloadUrl: (id: string): string =>
    `${BASE}/api/strukturku/activity/archives/${encodeURIComponent(id)}/download?auth=${encodeURIComponent(getToken() || "")}`,
};

export function fileUrl(path: string): string {
  return `${BASE}/api/strukturku/file/${path}?auth=${encodeURIComponent(getToken() || "")}`;
}

export const extractApi = {
  upload: async (file: File): Promise<{ id: string; filename: string; storage_path: string | null; extraction: any }> => {
    const fd = new FormData();
    fd.append("file", file);
    const headers: Record<string, string> = {};
    const t = getToken();
    if (t) headers["Authorization"] = "Bearer " + t;
    const res = await fetch(BASE + "/api/strukturku/extract", { method: "POST", headers, body: fd });
    let data: any = null;
    try { data = await res.json(); } catch { /* no body */ }
    if (!res.ok) throw { status: res.status, detail: data?.detail } as ApiError;
    // Ekstraksi berjalan async di server; polling status agar tidak kena timeout proxy (502).
    if (data?.status === "processing") {
      const id = data.id;
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        let sd: any = null;
        try {
          const s = await fetch(BASE + `/api/strukturku/extract/${encodeURIComponent(id)}`, { headers });
          sd = await s.json();
          if (!s.ok) continue;
        } catch { continue; }
        if (sd?.status === "done") return sd;
        if (sd?.status === "error") throw { status: 502, detail: sd?.error || "Gagal mengekstrak dokumen." } as ApiError;
      }
      throw { status: 504, detail: "Ekstraksi memakan waktu terlalu lama. Coba lagi atau gunakan dokumen lebih ringan." } as ApiError;
    }
    return data;
  },
};
